import { useEffect, useLayoutEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useAppUpdatePrompt } from 'hooks/useAppUpdatePrompt';
import useIcons from 'hooks/useIcons';
import { useThemeMode } from 'hooks/useThemeMode';
import AuthProvider from 'providers/AuthProvider';
import { useSettingsContext } from 'providers/SettingsProvider';
import { REFRESH } from 'reducers/SettingsReducer';

// Take ownership of scroll restoration so back/forward navigation doesn't
// race with our scroll-to-top below (otherwise iOS Safari can leave the page
// at a small positive scroll, hiding content behind the fixed AppBar).
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const scrollToTop = () => {
  window.scrollTo(0, 0);
  // Some browsers (notably iOS Safari) only scroll the documentElement, others
  // only the body — call both to be safe.
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
};

const App = () => {
  const { pathname } = useLocation();
  const { mode } = useThemeMode();
  const { configDispatch } = useSettingsContext();
  useIcons();

  // Long-lived tabs: detect a redeploy and offer a refresh (see hook).
  useAppUpdatePrompt();

  // Reset scroll on every route change. We do this in three phases because
  // iOS Safari can re-apply a cached scroll position *after* React's commit:
  //   1. Synchronously before paint (useLayoutEffect)
  //   2. On the next animation frame (after layout settles)
  //   3. On a macrotask (after Safari's async restoration finishes)
  useLayoutEffect(() => {
    scrollToTop();
    const rafId = requestAnimationFrame(scrollToTop);
    const timeoutId = window.setTimeout(scrollToTop, 0);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [pathname]);

  // Handle iOS Safari's back/forward cache (bfcache): when the page is
  // restored from cache, no React render happens, but pageshow fires with
  // persisted=true. Reset scroll there as well.
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) scrollToTop();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useLayoutEffect(() => {
    configDispatch({ type: REFRESH });
  }, [mode]);

  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
};

export default App;
